import * as Dialog from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { useContactForm } from "@/hooks/useContactForm";
import { cn } from "@/lib/cn";

interface ContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const stages = [
  "Just an idea",
  "Have an MVP",
  "Have users, need traction",
  "Need AI/automation layer",
  "Other",
];

export default function ContactDialog({ open, onOpenChange }: ContactDialogProps) {
  const {
    form,
    errors,
    isSubmitting,
    isSuccess,
    submitError,
    updateField,
    handleSubmit,
    reset,
  } = useContactForm(() => onOpenChange(false));

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-[32rem] -translate-x-1/2 -translate-y-1/2 rounded-sm border border-border bg-surface p-8 shadow-2xl data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out">
          <Dialog.Title className="font-display text-subsection font-semibold text-foreground">
            Let&rsquo;s build something.
          </Dialog.Title>
          <VisuallyHidden.Root>
            <Dialog.Description>
              Contact form to start a project with Rick Deacon
            </Dialog.Description>
          </VisuallyHidden.Root>

          <p className="mt-2 text-foreground-muted">
            Tell me about your idea and where you are. I&rsquo;ll respond within
            24 hours.
          </p>

          {/* Close button */}
          <Dialog.Close asChild>
            <button
              aria-label="Close"
              className="absolute right-4 top-4 text-foreground-muted transition-colors hover:text-foreground"
            >
              ✕
            </button>
          </Dialog.Close>

          {/* Success state */}
          {isSuccess ? (
            <div className="mt-8 flex flex-col items-center gap-3 py-8">
              <span className="text-3xl text-accent">✓</span>
              <p className="text-body-lg font-medium text-foreground">
                Got it. I&rsquo;ll be in touch.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 space-y-5" noValidate>
              {/* Name */}
              <Field
                label="Name"
                error={errors.name}
                required
              >
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  className={inputClass(errors.name)}
                  autoComplete="name"
                />
              </Field>

              {/* Email */}
              <Field
                label="Email"
                error={errors.email}
                required
              >
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  className={inputClass(errors.email)}
                  autoComplete="email"
                />
              </Field>

              {/* Company */}
              <Field label="Company / Project Name">
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => updateField("company", e.target.value)}
                  className={inputClass()}
                  autoComplete="organization"
                />
              </Field>

              {/* Stage */}
              <Field label="Stage">
                <select
                  value={form.stage}
                  onChange={(e) => updateField("stage", e.target.value)}
                  className={cn(inputClass(), !form.stage && "text-foreground-faint")}
                >
                  <option value="">Select your stage...</option>
                  {stages.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>

              {/* Message */}
              <Field
                label="Message"
                error={errors.message}
                required
              >
                <textarea
                  value={form.message}
                  onChange={(e) => updateField("message", e.target.value)}
                  className={cn(inputClass(errors.message), "min-h-[120px] resize-y")}
                  placeholder="What are you building? What's your biggest challenge right now?"
                  rows={4}
                />
              </Field>

              {/* Submit error */}
              {submitError && (
                <p className="text-small text-error">{submitError}</p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="group flex w-full items-center justify-center gap-2 border border-accent bg-transparent px-6 py-3 font-body text-cta uppercase text-accent transition-all duration-300 ease-out-expo hover:bg-accent hover:text-background hover:shadow-[0_0_20px_rgba(0,212,170,0.3)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Spinner />
                ) : (
                  <>
                    SEND
                    <span
                      aria-hidden="true"
                      className="inline-block transition-transform duration-300 group-hover:translate-x-1"
                    >
                      →
                    </span>
                  </>
                )}
              </button>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ── Field wrapper ── */

interface FieldProps {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}

function Field({ label, error, required, children }: FieldProps) {
  return (
    <div>
      <label className="mb-1.5 block text-label uppercase tracking-[0.05em] text-foreground-muted">
        {label}
        {required && <span className="ml-0.5 text-accent">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-small text-error">{error}</p>}
    </div>
  );
}

/* ── Input class builder ── */

function inputClass(error?: string) {
  return cn(
    "w-full rounded-sm border bg-surface-alt px-4 py-3 font-body text-foreground placeholder:text-foreground-faint transition-colors duration-200",
    "focus:outline-none focus:border-accent",
    error ? "border-error" : "border-border"
  );
}

/* ── Spinner ── */

function Spinner() {
  return (
    <svg
      className="h-5 w-5 animate-spin text-accent"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}
